import {
	CONSTANT,
	RATING_FORM_UI_SCHEMA_VERSION,
	RATING_FORM_UI_VERSION_TS1_V3,
	RATING_FORM_UI_VERSION_TS1_V4,
} from "src/app/constant";
import { DraftItem, TS1FormResponseJSON } from "src/app/models/models";
import { inferFormConfigVersion } from "./form-config-version.utils";
import { mapFormTypeCode } from "./form-type-code.utils";

/** Non-TS1 v2-bundle remote sign-off UI version (same numeric value as TS1 v3). */
export const REMOTE_SIGNOFF_V3 = RATING_FORM_UI_VERSION_TS1_V3;

const V2_BUNDLE_FORM_TYPES = new Set<string>([
	CONSTANT.FORM_TYPE.ts_6,
	CONSTANT.FORM_TYPE.ts_5,
	CONSTANT.FORM_TYPE.ts_3,
	CONSTANT.FORM_TYPE.cabin_crew,
	CONSTANT.FORM_TYPE.cabin_crew_instructor,
]);

const REMOTE_SIGNOFF_FORM_TYPES = new Set<string>([
	CONSTANT.FORM_TYPE.ts_3,
	CONSTANT.FORM_TYPE.cabin_crew,
	CONSTANT.FORM_TYPE.cabin_crew_instructor,
]);

/**
 * Primary signal: admin PF/PM form (`configVersion: 2` or observations with `candidateRole`).
 * See `inferFormConfigVersion` in form-config-version.utils.ts.
 */
function isTs1PfPmFormDetails(formDetails?: TS1FormResponseJSON | null): boolean {
	return inferFormConfigVersion(formDetails) === 2;
}

/**
 * Legacy fallback for drafts missing `ratingFormVersion` where `formDetails` is still
 * cached as admin v1 but the instructor already started the PF/PM shell (crew roles,
 * maneuver outcomes, TEM outcomes). Prefer hydrating formDetails on load instead; see
 * `prepareDraftForLoad` in container-ratings-form.component.ts.
 */
function hasUnmigratedPfPmDraftEvidence(draft: DraftItem): boolean {
	const cr = draft.candidateRating;
	if (!cr) return false;
	return (
		!!cr.crewRoleAssignment || (cr.maneuverOutcomes?.length ?? 0) > 0 || (cr.temCompetencyOutcomes?.length ?? 0) > 0
	);
}

/** Whether this TS-1 row should use the PF/PM shell (v3/v4), not legacy v2. */
function isTs1PfPmShell(formDetails?: TS1FormResponseJSON | null, draft?: DraftItem | null): boolean {
	if (isTs1PfPmFormDetails(formDetails)) return true;
	return draft != null && hasUnmigratedPfPmDraftEvidence(draft);
}

export function isRemoteSignoffFormType(formType?: string | null): boolean {
	const mapped = formType ? mapFormTypeCode(formType) : "";
	return !!mapped && REMOTE_SIGNOFF_FORM_TYPES.has(mapped);
}

function isRemoteSignoffEnabledOnForm(formDetails?: TS1FormResponseJSON | null): boolean {
	return formDetails?.remoteSignoffEnabled === true;
}

function resolveV2BundleRatingFormVersion(formType: string, formDetails?: TS1FormResponseJSON | null): number {
	if (isRemoteSignoffFormType(formType) && isRemoteSignoffEnabledOnForm(formDetails)) {
		return REMOTE_SIGNOFF_V3;
	}
	return RATING_FORM_UI_SCHEMA_VERSION;
}

/** Backfill version for drafts missing `ratingFormVersion` (legacy in-flight rows stay on v3). */
function resolveMissingRatingFormVersionForLoad(
	formType?: string,
	formDetails?: TS1FormResponseJSON | null,
	draft?: DraftItem | null,
): number | null {
	const mapped = formType ? mapFormTypeCode(formType) : "";
	if (mapped && V2_BUNDLE_FORM_TYPES.has(mapped)) {
		return resolveV2BundleRatingFormVersion(mapped, formDetails);
	}
	return resolveTs1RatingFormVersion(formType, formDetails, draft, RATING_FORM_UI_VERSION_TS1_V3);
}

/** Stamp once when a new draft row is created. */
export function resolveInitialRatingFormVersion(
	formType?: string,
	formDetails?: TS1FormResponseJSON | null,
	draft?: DraftItem | null,
): number | null {
	return resolveTs1RatingFormVersion(formType, formDetails, draft, RATING_FORM_UI_VERSION_TS1_V4);
}

function resolveTs1RatingFormVersion(
	formType: string | undefined,
	formDetails: TS1FormResponseJSON | null | undefined,
	draft: DraftItem | null | undefined,
	ts1PfPmVersion: number,
): number | null {
	const mapped = formType ? mapFormTypeCode(formType) : "";
	if (!mapped) return null;

	if (mapped === CONSTANT.FORM_TYPE.ts_1) {
		return isTs1PfPmShell(formDetails, draft) ? ts1PfPmVersion : RATING_FORM_UI_SCHEMA_VERSION;
	}

	if (V2_BUNDLE_FORM_TYPES.has(mapped)) {
		return resolveV2BundleRatingFormVersion(mapped, formDetails);
	}

	return null;
}

/** Read persisted version when loading a draft; one-time backfill for local-only rows missing version. */
export function resolveRatingFormVersionForLoad(draft: DraftItem): number | null {
	const version = draft.ratingFormVersion;
	if (version != null && !Number.isNaN(Number(version))) {
		return Number(version);
	}

	const formType = draft.formDetails?.formType?.code;
	return resolveMissingRatingFormVersionForLoad(formType, draft.formDetails, draft);
}

/** True when a loaded draft should mount the non-TS1 remote sign-off shell (ratingFormVersion 3). */
export function isRemoteSignoffV3ForLoad(draft: DraftItem): boolean {
	const version = resolveRatingFormVersionForLoad(draft);
	if (!isRemoteSignoffV3RatingFormVersion(version)) return false;
	const formType = draft.formDetails?.formType?.code;
	return isRemoteSignoffFormType(formType);
}

/** True when `ratingFormVersion === 3` for non-TS1 remote sign-off (not TS1 PF/PM). */
export function isRemoteSignoffV3RatingFormVersion(version: number | null | undefined): boolean {
	return Number(version) === REMOTE_SIGNOFF_V3;
}

/**
 * True when the instructor rating shell should use remote sign-off (4-step + send to candidate).
 * Matches backend eligibility: the persisted assessment version must already be 3.
 * Enabling remote sign-off on the form does not upgrade an in-flight version 2 draft.
 */
export function isRemoteSignoffShell(
	formType: string | undefined,
	ratingFormVersion: number | null | undefined,
	_formDetails?: TS1FormResponseJSON | null,
): boolean {
	if (!isRemoteSignoffFormType(formType)) return false;
	return isRemoteSignoffV3RatingFormVersion(ratingFormVersion);
}

/** True when load resolver derived a version that should be persisted once to IndexedDB. */
export function shouldPersistBackfilledRatingFormVersion(draft: DraftItem): boolean {
	if (draft.ratingFormVersion != null && !Number.isNaN(Number(draft.ratingFormVersion))) {
		return false;
	}
	return resolveRatingFormVersionForLoad(draft) != null;
}

export function isTs1V3RatingFormVersion(version: number | null | undefined): boolean {
	return Number(version) === RATING_FORM_UI_VERSION_TS1_V3;
}

export function isTs1V4RatingFormVersion(version: number | null | undefined): boolean {
	return Number(version) === RATING_FORM_UI_VERSION_TS1_V4;
}

/** TS1 v3 or v4 (PF/PM admin v2 rating shells). */
export function isTs1V3OrV4RatingFormVersion(version: number | null | undefined): boolean {
	const v = Number(version);
	return v === RATING_FORM_UI_VERSION_TS1_V3 || v === RATING_FORM_UI_VERSION_TS1_V4;
}

/**
 * TS1 PF/PM shells only. Version 3 is also used by non-TS1 remote sign-off, so a missing
 * or non-TS1 form type does not qualify unless the version is 4 (TS1-only).
 */
export function isTs1V3OrV4DraftAssessment(
	ratingFormVersion: number | null | undefined,
	formTypeCode?: string | null,
): boolean {
	if (isTs1V4RatingFormVersion(ratingFormVersion)) return true;
	if (!isTs1V3RatingFormVersion(ratingFormVersion)) return false;
	if (!formTypeCode) return false;
	return mapFormTypeCode(formTypeCode) === CONSTANT.FORM_TYPE.ts_1;
}
